import type { StageHistoryEntry } from '../kit/kit';
import type { ActivityEntry, ActivityType } from './activityType';

/**
 * One case's history: the bench entries with their durations and the activity
 * rows, as one timeline. Section 4.7 of the request of 13 September 2026.
 */

/**
 * The ways a case's history is read. Notes and Moves were added on 5 October
 * 2026: the Lab Notes written on the case, and where it was sent and to whom.
 */
export const CASE_HISTORY_FILTERS = ['all', 'bench', 'moves', 'notes', 'accounts', 'edits'] as const;
export type CaseHistoryFilter = (typeof CASE_HISTORY_FILTERS)[number];

/**
 * Which filter a row of each type belongs to. A manager opening a case with
 * sixty rows on it usually wants one of three things: what the benches did,
 * what happened to the money and the parcel, or what was changed. The rows that
 * belong to no case (signing in, administration, access) belong to no filter.
 */
const GROUP_OF: Record<ActivityType, Exclude<CaseHistoryFilter, 'all'> | null> = {
  scan: 'bench',
  done: 'bench',
  rework: 'bench',
  qc: 'bench',
  assign: 'bench',
  note: 'bench',
  print: 'bench',
  pay: 'accounts',
  hold: 'accounts',
  ship: 'accounts',
  intake: 'edits',
  edit: 'edits',
  date: 'edits',
  urgent: 'edits',
  photo: 'edits',
  delete: 'edits',
  redo: 'edits',
  // Archived, or taken out again. Section 4 of the request of 24 September 2026.
  archive: 'edits',
  admin: null,
  auth: null,
  access: null,
};

/**
 * The two narrower readings, across the groups above: a move or an assignment
 * is bench work too, and a note is too, so these rows answer to both.
 */
const ALSO_IN: Partial<Record<ActivityType, Exclude<CaseHistoryFilter, 'all'>>> = {
  assign: 'moves',
  done: 'moves',
  rework: 'moves',
  qc: 'moves',
  note: 'notes',
};

export function inHistoryFilter(type: ActivityType, filter: CaseHistoryFilter): boolean {
  return filter === 'all' || GROUP_OF[type] === filter || ALSO_IN[type] === filter;
}

export type CaseHistoryItem =
  | { kind: 'stage'; at: string; entry: StageHistoryEntry }
  | { kind: 'activity'; at: string; entry: ActivityEntry };

/**
 * The bench entries and the activity rows as one list, newest first.
 *
 * A bench entry stands at the moment it began: an entry still open began the
 * visit that is going on now, and one that closed is placed where the work
 * started, which is where the activity row for starting it sits too. Bench
 * entries are bench work, so the other two filters leave them out.
 */
export function caseTimeline(
  stages: readonly StageHistoryEntry[],
  activity: readonly ActivityEntry[],
  filter: CaseHistoryFilter,
): CaseHistoryItem[] {
  const items: CaseHistoryItem[] = [
    ...(filter === 'all' || filter === 'bench'
      ? stages.map((entry) => ({ kind: 'stage' as const, at: entry.startedAt, entry }))
      : []),
    ...activity
      .filter((entry) => inHistoryFilter(entry.type, filter))
      .map((entry) => ({ kind: 'activity' as const, at: entry.createdAt, entry })),
  ];

  // Newest first. At the same instant the row that says what somebody did
  // reads above the bench entry it opened.
  const rank = (item: CaseHistoryItem) => (item.kind === 'activity' ? 0 : 1);
  return items.sort((a, b) => b.at.localeCompare(a.at) || rank(a) - rank(b));
}
